import type { ReactNode, RefObject } from 'react';

import {
  Box,
  ButtonBase,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { Crop, Pencil, Trash2, UploadCloud } from 'lucide-react';

import { BRAND_GRADIENT } from '@modules/_shared/components/image-upload/brandGradient';
import { useLocale } from '@shared/hooks/locale';

const previewActionSx = {
  border: 1,
  borderColor: 'rgba(255,255,255,0.6)',
  borderRadius: '10px',
  bgcolor: 'rgba(255,255,255,0.92)',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
  '&:hover': { bgcolor: 'white' },
} as const;

type ImageOverlayActionsProps = {
  onCrop?: () => void;
  onReplace?: () => void;
  onRemove?: () => void;
  cropTooltip?: string;
  replaceTooltip?: string;
  removeTooltip?: string;
  disabled?: boolean;
};

// Small glassy icon buttons overlaid on the top-right of an image. Shared by the
// saved-image preview and the in-progress cropper so both expose the same
// change / remove (/ crop) affordances instead of large standalone buttons.
export const OnboardingImageOverlayActions = ({
  onCrop,
  onReplace,
  onRemove,
  cropTooltip,
  replaceTooltip,
  removeTooltip,
  disabled,
}: ImageOverlayActionsProps) => {
  const { t } = useLocale();

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ position: 'absolute', top: 12, right: 12, zIndex: 3 }}
    >
      {onCrop && (
        <Tooltip title={cropTooltip ?? t('Crop')} placement="top">
          <IconButton
            size="small"
            onClick={onCrop}
            disabled={disabled}
            sx={{ ...previewActionSx, color: 'text.primary' }}
          >
            <Crop size={16} />
          </IconButton>
        </Tooltip>
      )}
      {onReplace && (
        <Tooltip title={replaceTooltip ?? t('Change photo')} placement="top">
          <IconButton
            size="small"
            onClick={onReplace}
            disabled={disabled}
            sx={{ ...previewActionSx, color: 'text.primary' }}
          >
            <Pencil size={16} />
          </IconButton>
        </Tooltip>
      )}
      {onRemove && (
        <Tooltip title={removeTooltip ?? t('Remove')} placement="top">
          <IconButton
            size="small"
            onClick={onRemove}
            disabled={disabled}
            sx={{ ...previewActionSx, color: 'text.secondary' }}
          >
            <Trash2 size={16} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

type ImageSizingProps = {
  aspectRatio: number;
  minHeight?: { xs: number; md: number };
  maxHeight?: { xs: number; md: number };
  borderRadius?: string;
};

type DropzoneProps = ImageSizingProps & {
  targetRef: RefObject<HTMLButtonElement | null>;
  dragOver: boolean;
  isUploading: boolean;
  onClick: () => void;
  uploadLabel: string;
  uploadLabelDragOver: string;
  hintText: string;
};

export const OnboardingImageDropzone = ({
  targetRef,
  dragOver,
  isUploading,
  onClick,
  aspectRatio,
  minHeight = { xs: 200, md: 220 },
  maxHeight = { xs: 280, md: 320 },
  borderRadius = '16px',
  uploadLabel,
  uploadLabelDragOver,
  hintText,
}: DropzoneProps) => {
  const active = dragOver || isUploading;

  return (
    <ButtonBase
      ref={targetRef}
      onClick={onClick}
      disabled={isUploading}
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        width: '100%',
        aspectRatio: `${aspectRatio} / 1`,
        minHeight,
        maxHeight,
        borderRadius,
        border: '2px dashed',
        borderColor: active ? '#203097' : 'grey.300',
        bgcolor: active ? alpha('#56BEDB', 0.08) : 'background.paper',
        transition:
          'background-color 0.2s, border-color 0.2s, box-shadow 0.2s, transform 0.2s',
        cursor: isUploading ? 'default' : 'pointer',
        boxShadow: active ? '0 0 0 6px rgba(86,190,219,0.16)' : 'none',
        '&:hover': isUploading
          ? undefined
          : {
              borderColor: '#203097',
              bgcolor: alpha('#56BEDB', 0.06),
              transform: 'translateY(-1px)',
            },
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: BRAND_GRADIENT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 6px 16px rgba(32,48,151,0.22)',
        }}
      >
        {isUploading ? (
          <CircularProgress size={22} thickness={5} sx={{ color: 'white' }} />
        ) : (
          <UploadCloud size={26} strokeWidth={2} color="#ffffff" />
        )}
      </Box>
      <Box sx={{ textAlign: 'center', px: 2 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            fontSize: { xs: 15, md: 16 },
          }}
        >
          {dragOver ? uploadLabelDragOver : uploadLabel}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.5,
            color: 'text.secondary',
            fontSize: { xs: 12, md: 13 },
          }}
        >
          {hintText}
        </Typography>
      </Box>
    </ButtonBase>
  );
};

type PreviewProps = ImageSizingProps & {
  imageUrl: string;
  isUploading: boolean;
  onReplace: () => void;
  onRemove: () => void;
  onCrop?: () => void;
  cropTooltip?: string;
  replaceTooltip?: string;
};

type ImageFrameProps = {
  imageUrl: string;
  isUploading: boolean;
  /** Renders an accessible image rather than a decorative background image. */
  imageAlt?: string;
  imageFit?: 'cover' | 'contain';
  sx?: SxProps<Theme>;
  loadingOverlaySx?: SxProps<Theme>;
  children?: ReactNode;
};

export const OnboardingImageFrame = ({
  imageUrl,
  isUploading,
  imageAlt,
  imageFit = 'cover',
  sx,
  loadingOverlaySx,
  children,
}: ImageFrameProps) => {
  return (
    <Box
      sx={[
        {
          position: 'relative',
          overflow: 'hidden',
          ...(imageAlt === undefined
            ? {
                backgroundImage: `url("${imageUrl.replace(/"/g, '%22')}")`,
                backgroundSize: imageFit,
                backgroundPosition: 'center',
              }
            : {}),
          boxShadow: '0 8px 24px rgba(15,23,42,0.10)',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {imageAlt !== undefined && (
        <Box
          component="img"
          src={imageUrl}
          alt={imageAlt}
          sx={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: imageFit,
            display: 'block',
          }}
        />
      )}
      {children}
      {isUploading && (
        <Box
          sx={[
            {
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255,255,255,0.7)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
            ...(Array.isArray(loadingOverlaySx)
              ? loadingOverlaySx
              : [loadingOverlaySx]),
          ]}
        >
          <CircularProgress size={28} thickness={5} />
        </Box>
      )}
    </Box>
  );
};

export const OnboardingImagePreview = ({
  imageUrl,
  isUploading,
  onReplace,
  onRemove,
  onCrop,
  cropTooltip,
  aspectRatio,
  minHeight = { xs: 200, md: 220 },
  maxHeight = { xs: 280, md: 320 },
  borderRadius = '16px',
  replaceTooltip,
}: PreviewProps) => {
  return (
    <OnboardingImageFrame
      imageUrl={imageUrl}
      isUploading={isUploading}
      sx={{
        position: 'relative',
        width: '100%',
        aspectRatio: `${aspectRatio} / 1`,
        minHeight,
        maxHeight,
        borderRadius,
        overflow: 'hidden',
        bgcolor: 'grey.200',
        boxShadow: '0 8px 24px rgba(15,23,42,0.10)',
      }}
    >
      <OnboardingImageOverlayActions
        onCrop={onCrop}
        onReplace={onReplace}
        onRemove={onRemove}
        cropTooltip={cropTooltip}
        replaceTooltip={replaceTooltip}
        disabled={isUploading}
      />
    </OnboardingImageFrame>
  );
};
